import React, { useEffect, useMemo, useState } from "react";
import { fallbackArticles, fallbackContact, fallbackProfile } from "../lib/content";
import { loadPublicContent } from "../lib/data";
import { articleHref, findArticle } from "../lib/articles";
import { getArticleDetail } from "../lib/articleDetails";
import { LanguageSwitcher, localizedField, useI18n } from "../lib/i18n";
import { Brand, Icon } from "./Icons";

function articleParagraphs(value) {
  return String(value || "")
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function formatArticleDate(value, locale) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(date);
}

export default function ArticlePage({ identifier }) {
  const { language, locale, t } = useI18n();
  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;
    loadPublicContent()
      .then((result) => active && setContent(result))
      .catch((error) => {
        if (!active) return;
        setLoadError(error.message);
        setContent({
          connected: false,
          profile: fallbackProfile,
          contact: fallbackContact,
          branding: null,
          articles: fallbackArticles,
        });
      });
    return () => { active = false; };
  }, []);

  const articles = content?.articles?.length ? content.articles : fallbackArticles;
  const article = useMemo(() => findArticle(articles, identifier), [articles, identifier]);
  const profile = content?.profile || fallbackProfile;
  const contact = content?.contact || fallbackContact;
  const logoUrl = content?.branding?.logo_url || "";
  const detail = getArticleDetail(article, language);
  const paragraphs = detail?.intro ? [detail.intro] : articleParagraphs(localizedField(article, "content", language));
  const heroImage = article?.image_url || detail?.image || "";
  const articleDate = formatArticleDate(article?.created_at, locale);
  const phone = String(contact.phone || profile.phone || "").replace(/[^+\d]/g, "");

  useEffect(() => {
    const previous = document.title;
    document.title = article ? `${localizedField(article, "title", language)} | Juris Prudent` : `${t("Artikull")} | Juris Prudent`;
    return () => { document.title = previous; };
  }, [article, language, t]);

  return (
    <div className="article-page-shell">
      {content?.connectionWarning || loadError ? (
        <div className="connection-banner" role="status">
          <Icon name="warning" size={16} />
          <span>{t(content?.connectionWarning || loadError)}</span>
        </div>
      ) : null}

      <header className="site-header article-page-header">
        <a href="/" className="brand-link" aria-label={t("Kreu")}><Brand logoUrl={logoUrl} /></a>
        <LanguageSwitcher compact />
        <nav className="article-page-nav" aria-label={t("Navigimi i artikullit")}>
          <a href="/#artikujt"><Icon name="arrowLeft" size={16} /> {t("Të gjithë artikujt")}</a>
          <a className="button button--gold button--small" href="/#kontakt"><Icon name="calendar" size={15} /><span className="article-cta-long">{t("Rezervo Konsultë")}</span><span className="article-cta-short">{t("Konsultë")}</span></a>
        </nav>
      </header>

      <main className="article-page-main">
        {!content ? (
          <div className="article-loading"><span className="loader" /><p>{t("Duke hapur artikullin…")}</p></div>
        ) : article ? (
          <>
            <article className="article-detail">
              <div className="article-detail__visual" style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}>
                {!heroImage ? <Icon name={article.icon || "articles"} size={74} /> : null}
              </div>
              <div className="article-detail__content">
                <a className="article-back" href="/#artikujt"><Icon name="arrowLeft" size={15} /> {t("Kthehu te këshillat ligjore")}</a>
                <span className="eyebrow">{t("Këshilla Ligjore — Juris Prudent")}</span>
                <h1>{localizedField(article, "title", language)}</h1>
                {articleDate ? <time dateTime={article.created_at}>{articleDate}</time> : null}
                <div className="article-copy">
                  {(paragraphs.length ? paragraphs : [t("Përmbajtja e këtij artikulli do të publikohet së shpejti.")]).map((paragraph, index) => <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>)}
                </div>
                <div className="article-disclaimer">
                  <Icon name="shield" size={22} />
                  <p>{t("Ky material është informues dhe nuk zëvendëson këshillën juridike të përshtatur për rrethanat e rastit tuaj.")}</p>
                </div>
                <div className="article-detail__actions">
                  <a className="button button--gold" href="/#kontakt"><Icon name="calendar" size={17} /> {t("Kërko Këshillë")}</a>
                  <a className="button button--outline" href={`tel:${phone}`}><Icon name="phone" size={17} /> {t("Na Kontaktoni")}</a>
                </div>
              </div>
            </article>

            {detail?.highlights?.length ? (
              <section className="article-highlights" aria-label={t("Hapat kryesorë")}>
                {detail.highlights.map((item) => (
                  <div key={item.value}>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </section>
            ) : null}

            {detail ? (
              <section className="article-body-layout">
                <div className="article-body-main">
                  {detail.sections.map((section, index) => (
                    <section className="article-section" id={`pjesa-${index + 1}`} key={section.title}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <h2>{section.title}</h2>
                      {(section.paragraphs || []).map((paragraph) => <p key={paragraph.slice(0, 40)}>{paragraph}</p>)}
                      {section.bullets?.length ? (
                        <ul>
                          {section.bullets.map((item) => <li key={item}><Icon name="check" size={16} /><span>{item}</span></li>)}
                        </ul>
                      ) : null}
                    </section>
                  ))}

                  <div className="article-closing-cta">
                    <span className="eyebrow">{t("Keni një çështje konkrete?")}</span>
                    <h2>{t("Dokumentet dhe afatet ndryshojnë sipas rastit.")}</h2>
                    <p>{t("Rezervoni një konsultë për vlerësim të dokumenteve, rreziqeve dhe hapave të përshtatshëm për situatën tuaj.")}</p>
                    <a className="button button--gold" href="/#kontakt"><Icon name="calendar" size={17} /> {t("Rezervo Konsultë")}</a>
                  </div>
                </div>

                <aside className="article-sidebar">
                  <div className="article-sidebar__sticky">
                    <section>
                      <span className="eyebrow">{detail.checklistTitle}</span>
                      <ul className="article-checklist">
                        {detail.checklist.map((item) => <li key={item}><Icon name="check" size={15} /><span>{item}</span></li>)}
                      </ul>
                    </section>
                    <section>
                      <span className="eyebrow">{t("Burime Zyrtare")}</span>
                      <p className="article-sources-note">{t("Lidhjet më poshtë hapin aktet përkatëse në Gazetën Zyrtare të Republikës së Kosovës.")}</p>
                      <div className="article-sources">
                        {detail.sources.map((source) => (
                          <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>
                            <Icon name="external" size={14} /><span>{source.label}</span>
                          </a>
                        ))}
                      </div>
                    </section>
                  </div>
                </aside>
              </section>
            ) : null}

            <section className="related-articles" aria-labelledby="related-title">
              <div className="section-heading">
                <span>{t("Lexoni Gjithashtu")}</span>
                <h2 id="related-title">{t("Këshilla të tjera ligjore")}</h2>
              </div>
              <div className="related-articles__grid">
                {articles.filter((item) => item !== article).slice(0, 2).map((item) => (
                  <a href={articleHref(item)} key={item.id || item.title}>
                    <Icon name={item.icon || "articles"} size={24} />
                    <span><strong>{localizedField(item, "title", language)}</strong><small>{t("Lexo artikullin")}</small></span>
                    <Icon name="arrow" size={15} />
                  </a>
                ))}
              </div>
            </section>
          </>
        ) : (
          <section className="article-not-found">
            <span className="icon-badge"><Icon name="document" size={29} /></span>
            <h1>{t("Artikulli nuk u gjet")}</h1>
            <p>{t("Lidhja mund të jetë ndryshuar ose artikulli nuk është më i publikuar.")}</p>
            <a className="button button--gold" href="/#artikujt">{t("Shiko të gjithë artikujt")}</a>
          </section>
        )}
      </main>

      <footer className="site-footer article-page-footer">
        <Brand logoUrl={logoUrl} compact />
        <p>© {new Date().getFullYear()} Juris Prudent Studio Ligjore.</p>
        <a href="/#kontakt">{t("Kontakt")}</a>
      </footer>
    </div>
  );
}
